import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { useWebSocket } from '../../hooks/useWebSocket';
import AuthModal from '../auth/AuthModal';
import { useUniversity } from '../../contexts/UniversityContext';
import messagesService from '../../api/messages';

const ComposerContainer = styled.div`
  background: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 15px;
  margin-bottom: 20px;
  transition: box-shadow 0.2s ease-in-out, opacity 0.3s ease-out;
  position: relative;
  z-index: 1;
  transform: translateZ(0); /* Forzar aceleración por hardware */
  will-change: transform, opacity;
  contain: layout style;
  min-height: 115px;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'translateY(0)' : 'translateY(-10px)'};
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 15px;
    min-height: 100px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    margin-bottom: 12px;
    min-height: 90px;
  }

  @media (max-height: 600px) {
    padding: 8px;
    margin-bottom: 8px;
    min-height: auto;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1877f2, #4267B2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  flex-shrink: 0;
  font-size: 22px;
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 20px;
    margin-right: 8px;
  }
  
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 18px;
    margin-right: 6px;
  }
  
  @media (max-height: 600px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
    margin-right: 5px;
  }
`;

const TextArea = styled.textarea`
  flex-grow: 1;
  border: none;
  resize: none;
  padding: 10px;
  font-size: 15px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  min-height: 60px;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    padding: 8px;
    font-size: 16px;
    min-height: 50px;
  }
  
  @media (max-width: 480px) {
    padding: 6px;
    min-height: 40px;
    font-size: 16px;
  }

  @media (max-height: 600px) {
    min-height: 35px;
    padding: 5px;
    font-size: 16px;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    font-size: 15px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
      content: "🎭 Comparte anónimamente...";
    }
    
    @media (max-width: 360px) {
      font-size: 16px;
      content: "🎭 Escribe algo...";
    }
  }
  
  &:focus {
    background-color: ${props => props.theme.inputBgFocus};
    transform: scale(1);
    
    &::placeholder {
      opacity: 0.7;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 10px;
  padding: 8px;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: 4px;
  text-align: center;
`;

// Special styled component for rate limiting errors
const RateLimitError = styled(ErrorMessage)`
  background-color: rgba(241, 196, 15, 0.1);
  color: #d35400;
  border-left: 3px solid #f39c12;
  padding-left: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  span.icon {
    font-size: 16px;
    margin-bottom: 4px;
  }
  
  span.message {
    font-weight: 500;
  }
  
  @media (max-width: 480px) {
    span.icon {
      font-size: 14px;
    }
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 50px;
  width: calc(100% - 50px);
  box-sizing: border-box;
  gap: 10px;
  
  @media (max-width: 768px) {
    margin-left: 45px;
    width: calc(100% - 45px);
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    margin-left: 40px;
    width: calc(100% - 40px);
    gap: 6px;
  }

  @media (max-height: 600px) {
    margin-left: 35px;
    width: calc(100% - 35px);
    gap: 5px;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  
  @media (max-width: 768px) {
    padding: 7px 14px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 4px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AttachButton = styled(Button)`
  background-color: ${props => props.theme.buttonLight};
  color: ${props => props.theme.buttonText};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.buttonLightHover};
  }
  
  i {
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 15px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 7px 14px;
    font-size: 13px;
    gap: 5px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
    gap: 4px;
  }
  
  @media (max-height: 600px) {
    padding: 5px 10px;
    font-size: 12px;
  }
`;

const SendButton = styled.button`
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: bold;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.primaryHover};
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 7px 14px;
    font-size: 13px;
    gap: 5px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
    gap: 4px;
  }
  
  @media (max-height: 600px) {
    padding: 5px 10px;
    font-size: 12px;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  margin-left: 50px;
  margin-bottom: 10px;
  max-width: 250px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dddfe2;
  width: calc(100% - 50px);
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-left: 45px;
    max-width: 200px;
    width: calc(100% - 45px);
  }
  
  @media (max-width: 480px) {
    margin-left: 40px;
    max-width: 150px;
    width: calc(100% - 40px);
  }

  @media (max-height: 600px) {
    margin-left: 35px;
    max-width: 120px;
    width: calc(100% - 35px);
    margin-bottom: 5px;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  display: block;
  
  @media (max-width: 768px) {
    max-height: 120px;
  }
  
  @media (max-width: 480px) {
    max-height: 100px;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    top: 3px;
    right: 3px;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

const PromptIcon = styled.div`
  font-size: 24px;
  margin-bottom: 10px;
`;

const PromptText = styled.div`
  font-size: 16px;
`;

const UniversityRequiredPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`;

const MessageComposer = ({ onAuthNeeded }) => {
  const { isAuthenticated, isTokenExpired, user } = useAuth();
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [sending, setSending] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState(false);
  const { connected } = useWebSocket();
  const { currentUniversity } = useUniversity();
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const cooldownInterval = useRef(null); // Referencia para el intervalo de cooldown
  const [visible, setVisible] = useState(false);

  // Definir constantes para los límites de longitud del mensaje
  const MAX_MESSAGE_LENGTH = 500;
  const MIN_MESSAGE_LENGTH = 1;

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  const handleMessageChange = (e) => {
    // Validar longitud máxima mientras el usuario escribe
    if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
      setMessage(e.target.value);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleImageClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen es demasiado grande. Máximo 5MB.');
        return;
      }
      
      // Validar tipo
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Formato de imagen no válido. Use JPEG, PNG, GIF o WEBP.');
        return;
      }
      
      // Comprimir la imagen antes de establecerla (opcional)
      setImage(file);
      setError(null);
      
      // Crear URL para previsualización
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    
    // Verificar si el token ha expirado
    if (isTokenExpired && isTokenExpired()) {
      setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      setShowAuthModal(true);
      return;
    }
    
    // Verificar si el usuario pertenece a la universidad seleccionada
    if (user && user.university !== currentUniversity?.id) {
      setError(`Solo puedes publicar en tu universidad: ${user.university.toUpperCase()}`);
      return;
    }
    
    if (sending) return;
    
    // Normalizar el texto: eliminar espacios en blanco al inicio y final
    const trimmedMessage = message.trim();
    
    // Limitar los saltos de línea consecutivos a un máximo de 2
    const normalizedMessage = trimmedMessage.replace(/\n{3,}/g, '\n\n');
    
    // Validar contenido y longitud del mensaje
    if ((!normalizedMessage && !image) || (normalizedMessage.length < MIN_MESSAGE_LENGTH && !image)) {
      setError('Por favor escribe un mensaje o adjunta una imagen');
      return;
    }
    
    // Validar longitud máxima del mensaje
    if (normalizedMessage.length > MAX_MESSAGE_LENGTH) {
      setError(`El mensaje no puede exceder los ${MAX_MESSAGE_LENGTH} caracteres`);
      return;
    }
    
    // Validar imagen antes de intentar subir
    if (image) {
      // Verificar tamaño nuevamente (máximo 5MB)
      if (image.size > 5 * 1024 * 1024) {
        setError('La imagen es demasiado grande. Máximo 5MB.');
        return;
      }
      
      // Verificar tipo nuevamente
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(image.type)) {
        setError('Formato de imagen no válido. Use JPEG, PNG, GIF o WEBP.');
        return;
      }
    }
    
    setError(null);
    setSending(true);
    
    try {
      // Crear FormData para envío multipart
      const formData = new FormData();
      formData.append('text', normalizedMessage);
      
      if (image) {
        // Usar SOLO 'image' como nombre del campo para que coincida con lo que espera el backend
        formData.append('image', image);
      }
      
      // Añadir universidad al FormData
      if (currentUniversity) {
        formData.append('university', currentUniversity.id);
      }
      
      // Enviar mensaje usando el servicio
      await messagesService.createMessage(formData);
      
      // Limpiar formulario tras éxito
      setMessage('');
      setImage(null);
      setImagePreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error al publicar mensaje:', err);
      
      // Check for rate limiting errors
      if (err.message && (
          err.message.includes('excedido el límite') || 
          err.message.includes('demasiado rápido')
      )) {
        setError(err.message);
        return;
      }
      
      // Información más detallada para depuración
      if (err.response) {
        console.error('Respuesta de error:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
      }
      
      // Verificar si es un error de autenticación
      if (err.response && err.response.status === 401) {
        setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        setShowAuthModal(true);
      } else if (err.response && err.response.status === 400) {
        // Mensajes específicos para errores 400
        if (err.response.data && err.response.data.message) {
          setError(err.response.data.message);
        } else if (image) {
          setError('Error al procesar la imagen. Intenta con otro formato o una imagen más pequeña.');
        } else {
          setError('La solicitud contiene datos inválidos. Por favor revisa el contenido.');
        }
      } else if (err.response && err.response.status === 403) {
        // Nuevo manejo para contenido inapropiado
        setError('La imagen contiene contenido inapropiado que no está permitido.');
      } else if (err.response && err.response.status === 429) {
        // Error de límite de velocidad desde el servidor
        setError(err.response.data || 'Has alcanzado el límite de publicaciones. Inténtalo más tarde.');
      } else {
        setError(err.response?.data?.message || 'Error al publicar el mensaje. Intenta de nuevo.');
      }
    } finally {
      setSending(false);
    }
  };
  
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };
  
  // Añadir un efecto de limpieza cuando el componente se desmonta
  useEffect(() => {
    // Capturar los valores de ref actuales en variables locales
    const currentCooldownInterval = cooldownInterval.current;
    const currentFileInputRef = fileInputRef.current;
    
    return () => {
      // Limpiar cualquier estado o efecto cuando el componente se desmonta
      if (currentCooldownInterval) {
        clearInterval(currentCooldownInterval);
      }
      if (currentFileInputRef) {
        currentFileInputRef.value = '';
      }
    };
  }, []);
  
  // Efecto para controlar la visibilidad con un pequeño retraso
  useEffect(() => {
    // Aumentar el retraso para permitir que el DOM se estabilice
    const timer = setTimeout(() => {
      setVisible(true);
    }, 250);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      {/* No mostrar nada si hay universidad seleccionada pero no está autenticado */}
      {(!currentUniversity || isAuthenticated) ? (
        <ComposerContainer 
          style={focused ? { boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)' } : {}}
          className="message-composer-container"
          $visible={visible}
        >
          {!currentUniversity ? (
            <UniversityRequiredPrompt>
              <PromptIcon>🏛️</PromptIcon>
              <PromptText>
                Selecciona una universidad para comenzar a interactuar
              </PromptText>
            </UniversityRequiredPrompt>
          ) : (
            <>
              <Form onSubmit={handleSubmit}>
                {/* Mostrar mensaje de error si el usuario no pertenece a esta universidad */}
                {user && user.university !== currentUniversity.id && (
                  <ErrorMessage>
                    Solo puedes publicar en tu universidad: {user.university.toUpperCase()}
                  </ErrorMessage>
                )}
                
                {error && (
                  error.includes('excedido el límite') || error.includes('demasiado rápido') || error.includes('alcanzado el límite') ? (
                    <RateLimitError>
                      <span className="icon">⏱️</span>
                      <span className="message">{error}</span>
                    </RateLimitError>
                  ) : (
                    <ErrorMessage>{error}</ErrorMessage>
                  )
                )}
                
                <TextAreaWrapper>
                  <UserAvatar style={{ 
                    background: 'linear-gradient(45deg, #1877f2, #4267B2)', 
                    fontSize: '22px',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
                  }}>
                    🐸
                  </UserAvatar>
                  
                  <TextArea
                    ref={textAreaRef}
                    value={message}
                    onChange={handleMessageChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={
                      window.innerWidth <= 360 ? `🎭 Escribe #${currentUniversity.initials}...` :
                      window.innerWidth <= 480 ? `🎭 Escribe #${currentUniversity.initials}...` :
                      `🎭 Escribe #${currentUniversity.initials}...`
                    }
                    disabled={sending || (user && user.university !== currentUniversity.id)}
                    maxLength={MAX_MESSAGE_LENGTH}
                    id="message-text"
                    name="message-text"
                  />
                </TextAreaWrapper>
                
                {/* Mostrar indicador de caracteres cuando el usuario se acerca al límite */}
                {message.length > MAX_MESSAGE_LENGTH * 0.7 && (
                  <div style={{ 
                    textAlign: 'right', 
                    fontSize: '12px', 
                    color: message.length > MAX_MESSAGE_LENGTH * 0.9 ? '#e74c3c' : '#666',
                    margin: '0 12px 8px 0'
                  }}>
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </div>
                )}
                
                {imagePreview && (
                  <ImagePreviewContainer>
                    <ImagePreview src={imagePreview} alt="Vista previa" />
                    <RemoveImageButton onClick={removeImage} type="button">×</RemoveImageButton>
                  </ImagePreviewContainer>
                )}
                
                <ButtonsRow>
                  <AttachButton 
                    type="button"
                    onClick={handleImageClick}
                    disabled={sending || (user && user.university !== currentUniversity.id)}
                  >
                    <i className="fas fa-image"></i>
                    Adjuntar imagen
                  </AttachButton>
                  
                  <SendButton 
                    type="submit" 
                    disabled={
                      sending || 
                      (!message.trim() && !image) || 
                      !connected || 
                      (user && user.university !== currentUniversity.id)
                    }
                  >
                    {sending ? 'Publicando...' : 'Publicar'}
                  </SendButton>
                </ButtonsRow>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  id="message-image"
                  name="message-image"
                />
              </Form>
            </>
          )}
        </ComposerContainer>
      ) : null}
      
      {/* Modal de Autenticación */}
      <AuthModal show={showAuthModal} onClose={closeAuthModal} initialTab="login" />
    </>
  );
};

// Exportación con un identificador de componente claro para debugging
MessageComposer.displayName = 'MessageComposer';

export default MessageComposer;